/**
 * Loads the plan list backing the report plan picker (In-Shift and End-of-Shift).
 *
 * The In-Shift report is scoped to the shift the operator picked, so it fetches
 * one shift. The End-of-Shift report covers both shifts at once (it has its own
 * Day / Night / Combined toggle), so it fetches both and merges — the grouping
 * helper dedupes by plan id.
 *
 * Selection is deliberately NOT owned here: the report pages keep the picked plan
 * so that "nothing picked yet" can fall back to the baseline the backend
 * resolved, without the fetch → baseline → selection → fetch feedback loop that
 * owning the default here would create.
 */
import { useCallback, useEffect, useState } from 'react';
import { getAllShiftPlans } from '../../../../services/shiftPlanService';
import { ShiftType, SPMSGetShiftPlanResponse } from '../../../../types/shiftPlan';
import { buildReportPlanGroups, ReportPlanGroup } from './reportPlanOptions';

export interface UseReportPlansArgs {
  /** OFD date the report is being viewed for. */
  ofdDate?: string;
  /** Shifts to pull plans for. One for In-Shift, both for End-of-Shift. */
  shiftTypes: ShiftType[];
  /** Skip fetching (e.g. no warehouse selected yet). */
  skip?: boolean;
}

export interface UseReportPlans {
  groups: ReportPlanGroup[];
  loading: boolean;
}

export const useReportPlans = ({
  ofdDate,
  shiftTypes,
  skip = false,
}: UseReportPlansArgs): UseReportPlans => {
  const [groups, setGroups] = useState<ReportPlanGroup[]>([]);
  const [loading, setLoading] = useState(false);

  // Joined so the effect re-runs on content change rather than array identity —
  // callers build the array inline on every render.
  const shiftKey = shiftTypes.join(',');

  // `isCurrent` lets a superseded run drop its result instead of overwriting the
  // state a later run already wrote: the operator can change date or shift while a
  // fetch is in flight, and responses are not guaranteed to land in request order.
  const load = useCallback(async (isCurrent: () => boolean) => {
    if (skip || !ofdDate || !shiftKey) {
      setGroups([]);
      // Also clear `loading`: a superseded in-flight run skips its own
      // `setLoading(false)` (its `isCurrent()` is already false), so this run is
      // the only one left that can take the spinner down.
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const shifts = shiftKey.split(',') as ShiftType[];
      // The picker is an optional control on a report that renders fine without
      // it, so a failed fetch must not raise the global error dialog over the
      // report — it degrades to an empty plan list instead.
      const responses = await Promise.all(
        shifts.map((shift) =>
          getAllShiftPlans(ofdDate, shift, undefined, undefined, {
            suppressErrorDialog: true,
          }).catch(() => null)
        )
      );
      // reduce rather than flatMap: the build targets a lib without Array.flatMap.
      const plans = responses.reduce<SPMSGetShiftPlanResponse[]>(
        (all, response) => all.concat(response?.shiftPlans ?? []),
        []
      );
      if (isCurrent()) {
        setGroups(buildReportPlanGroups(plans));
      }
    } finally {
      if (isCurrent()) {
        setLoading(false);
      }
    }
  }, [ofdDate, shiftKey, skip]);

  useEffect(() => {
    let cancelled = false;
    load(() => !cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  return { groups, loading };
};
